"use client";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Icons } from "@/components/Icons";
import { Button, Card } from "@/components/ui";
import type { ApologyOffer } from "@/api/reviewApi";

type ApologyOfferCardProps = {
  offer: ApologyOffer;
  /** Optional human-readable value line, e.g. "EGP 50 off your next order". Falls back to a generic label. */
  description?: string;
  onUseNow?: () => void;
};

/**
 * "Something for the trouble" — renders after a negative-review submit when
 * the backend attaches an apology offer. Null in M1 (backend doesn't issue
 * offers yet), so this only mounts once that field is populated — see
 * ReviewSubmitScreen's conditional render.
 */
export function ApologyOfferCard({ offer, description, onUseNow }: ApologyOfferCardProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = React.useState(false);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(offer.code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable — the code is still visible to copy manually */
    }
  };

  return (
    <Card
      padding={18}
      style={{
        width: "100%",
        textAlign: "start",
        border: "1px solid var(--primary-200, #c4ebd7)",
        boxShadow: "0 4px 16px -8px rgba(9,145,74,.25)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 10 }}>
        <Icons.spark size={17} stroke="var(--primary-600)" />
        <span
          style={{
            fontSize: 12.5,
            fontWeight: 700,
            letterSpacing: 0.02,
            color: "var(--primary-700)",
            textTransform: "uppercase",
          }}
        >
          {t("v2.reviews.apology.eyebrow", "Something for the trouble")}
        </span>
      </div>
      <div className="display" style={{ fontWeight: 700, fontSize: 19, color: "var(--ink-1)", marginBottom: 3 }}>
        {description ?? t("v2.reviews.apology.default_description", "A discount on your next order")}
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-2)", marginBottom: 14 }}>
        {t("v2.reviews.apology.code_label", "Code")}{" "}
        <span className="num" style={{ color: "var(--primary-700)" }}>{offer.code}</span>
        {offer.expiresAt && (
          <>
            {" "}
            · {t("v2.reviews.apology.expires", "Expires {{date}}", {
              date: new Date(offer.expiresAt).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
            })}
          </>
        )}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <Button variant="soft" size="md" full onClick={copyCode} leading={<Icons.copy size={15} />}>
          {copied ? t("v2.reviews.apology.copied", "Copied") : t("v2.reviews.apology.copy_code", "Copy code")}
        </Button>
        <Button variant="primary" size="md" full onClick={onUseNow}>
          {t("v2.reviews.apology.use_now", "Use now")}
        </Button>
      </div>
    </Card>
  );
}
